import { cn } from 'cn';
import {
  LinkButton,
  type LinkButtonProps,
} from '#ui/components/Button/LinkButton';
import type { NotificationType, SizeType } from '#ui/constants';
import Button, { BaseButton, type ButtonProps } from '../Button';
import { Icon } from '../Icon';
import {
  notificationIconColorClasses,
  notificationIconTypes,
  notificationLinkColorClasses,
} from './private/notification-appearance';

type TopCta =
  | {
      linkTop?: LinkButtonProps;
      buttonTop?: never;
    }
  | {
      linkTop?: never;
      buttonTop?: ButtonProps;
    };

export type InlineNotificationProps = {
  type?: NotificationType;
  size?: SizeType;
  message?: string | React.ReactNode;
  headline?: string | React.ReactNode;
  label?: string;
  link?: LinkButtonProps;
  className?: string;
  containerClassName?: string;
  children?: React.ReactNode;
  onDismiss?: () => void;
} & TopCta;

const baseClasses = cn('relative w-full rounded p-2 text-neutral-200');

const theme: Record<NotificationType, string> = {
  default: cn('bg-neutral-5'),
  info: cn('border-blue-40 bg-blue-5'),
  success: cn('border-green-40 bg-green-5'),
  warning: cn('border-orange-40 bg-orange-5'),
  danger: cn('border-red-40 bg-red-5'),
};

const roles: Record<NotificationType, 'alert' | 'banner'> = {
  default: 'banner',
  info: 'banner',
  success: 'banner',
  warning: 'banner',
  danger: 'alert',
};

const sizes: Record<SizeType, string> = {
  tiny: '', // cn('max-w-[320px]'),
  small: '', // cn('max-w-[400px]'),
  medium: cn('max-w-150'),
  large: cn('max-w-188'),
  xlarge: cn('max-w-242'),
  full: cn('w-full'),
};

const iconSizes: Record<SizeType, 'tiny' | 'small'> = {
  tiny: 'small',
  small: 'tiny',
  medium: 'small',
  large: 'small',
  xlarge: 'small',
  full: 'small',
};

const headlineSizes: Record<SizeType, string> = {
  tiny: 'typ-headline-sm',
  small: 'typ-headline-2xs',
  medium: 'typ-headline-sm',
  large: 'typ-headline-sm',
  xlarge: 'typ-headline-sm',
  full: 'typ-headline-sm',
};

const iconTextGaps: Record<SizeType, string> = {
  tiny: 'gap-x-2',
  small: 'gap-x-1',
  medium: 'gap-x-2',
  large: 'gap-x-4',
  xlarge: 'gap-x-2',
  full: 'gap-x-2',
};

export function InlineNotification({
  type = 'default',
  size = 'small',
  headline,
  label,
  linkTop,
  link,
  message,
  className,
  onDismiss,
  containerClassName,
  children,
  buttonTop,
  ...rest
}: InlineNotificationProps) {
  const iconType = notificationIconTypes[type];

  return (
    <div
      role={roles[type]}
      className={cn(baseClasses, sizes[size], theme[type], className)}
      {...rest}
    >
      <div className="absolute right-3 flex items-center justify-center gap-x-2.5">
        {linkTop ? (
          <LinkButton
            {...linkTop}
            compact
            className={cn(
              'typ-link-xs p-0 font-light',
              notificationLinkColorClasses[type],
            )}
          />
        ) : null}
        {typeof onDismiss === 'function' ? (
          <BaseButton onClick={onDismiss}>
            <Icon type="cross" size="tiny" />
          </BaseButton>
        ) : null}
      </div>
      <div className="flex gap-x-2">
        <div className={cn('flex grow flex-row', iconTextGaps[size])}>
          {iconType !== undefined ? (
            <div
              className={cn(notificationIconColorClasses[type], 'self-start')}
            >
              <Icon type={iconType} size={iconSizes[size]} solid />
            </div>
          ) : null}
          <div className={cn('flex-1', containerClassName)}>
            <div>
              {headline ? (
                <span className={cn('block', headlineSizes[size])}>
                  {headline}
                </span>
              ) : null}
              {label ? (
                <span className="typ-ui text-neutral-100">{label}</span>
              ) : null}
              {message ? (
                <p className="typ-ui-sm mt-1 whitespace-pre-line">{message}</p>
              ) : null}
              {link ? (
                <LinkButton
                  {...link}
                  compact
                  className={cn(
                    'typ-link-xs mt-2 p-0 font-light',
                    notificationLinkColorClasses[type],
                  )}
                />
              ) : null}
            </div>
            {children}
          </div>
        </div>
        {buttonTop ? (
          <div className="ml-auto whitespace-nowrap">
            <Button {...buttonTop} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
